/** 報告保存狀態（idle 尚未保存 / loading 保存中 / success 已保存 / error 失敗） */
export type SaveStatus = 'idle' | 'loading' | 'success' | 'error';

/** 歷史報告讀回狀態（idle / loading 讀取中 / error 讀取失敗） */
export type ReadbackStatus = 'idle' | 'loading' | 'error';

interface ReportSaveSectionProps {
  status: SaveStatus;
  /** 失敗時顯示的訊息（已對應為一般人可理解的文字） */
  errorMessage: string | null;
  /** 後端回報 reused = true（同一份報告已保存過，未重複建立） */
  reused: boolean;
  /** 使用者主動點擊保存 / 重新嘗試保存 */
  onSave: () => void;
  /** 保存成功後取得的 reportToken；有值才顯示「讀取已保存報告」 */
  reportToken: string | null;
  /** 讀取已保存報告的狀態 */
  readbackStatus: ReadbackStatus;
  /** 讀取失敗時顯示的訊息 */
  readbackError: string | null;
  /** 使用者點擊「讀取已保存報告」 */
  onReadback: () => void;
}

/**
 * 手動保存健診報告（Batch 7-B2A）+ 讀取已保存報告（Batch 7-B2B）。
 * V1 不自動保存：只有使用者主動點擊才呼叫 Backend save_gmb_audit。
 */
export default function ReportSaveSection({
  status,
  errorMessage,
  reused,
  onSave,
  reportToken,
  readbackStatus,
  readbackError,
  onReadback,
}: ReportSaveSectionProps) {
  const canReadback = status === 'success' && Boolean(reportToken);

  return (
    <section className="ds-container pt-16 md:pt-20">
      <div className="rounded-2xl border border-primary-200 bg-background-50 p-5 shadow-card md:p-7">
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div className="flex items-start gap-3.5">
            <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-primary-50 text-primary-700 ring-1 ring-primary-200">
              <i className="ri-save-3-line text-xl" />
            </span>
            <div className="min-w-0">
              <h2 className="text-lg font-bold text-foreground-950 md:text-xl">保存這次健診</h2>
              <p className="mt-1.5 text-base leading-relaxed text-foreground-600">
                保存本次結果，方便之後在此瀏覽器查看變化。同一份報告重複點擊不會重複建立。
              </p>
            </div>
          </div>

          {status === 'idle' && (
            <button
              type="button"
              onClick={onSave}
              className="inline-flex h-14 w-full cursor-pointer items-center justify-center gap-2 whitespace-nowrap rounded-xl bg-primary-700 px-6 text-base font-semibold text-background-50 transition-colors hover:bg-primary-800 sm:w-auto"
            >
              <i className="ri-save-3-line text-lg" />
              保存這次健診
            </button>
          )}

          {status === 'loading' && (
            <button
              type="button"
              disabled
              className="inline-flex h-14 w-full cursor-not-allowed items-center justify-center gap-2 whitespace-nowrap rounded-xl bg-primary-700/70 px-6 text-base font-semibold text-background-50 sm:w-auto"
            >
              <i className="ri-loader-4-line animate-spin text-lg" />
              正在保存…
            </button>
          )}

          {status === 'success' && (
            <span className="ds-badge bg-primary-100 text-primary-900 ring-1 ring-primary-200">
              <i className="ri-checkbox-circle-line text-lg" />
              這次健診已保存
            </span>
          )}

          {status === 'error' && (
            <button
              type="button"
              onClick={onSave}
              className="inline-flex h-14 w-full cursor-pointer items-center justify-center gap-2 whitespace-nowrap rounded-xl bg-primary-700 px-6 text-base font-semibold text-background-50 transition-colors hover:bg-primary-800 sm:w-auto"
            >
              <i className="ri-refresh-line text-lg" />
              重新嘗試保存
            </button>
          )}
        </div>

        {status === 'success' && (
          <div className="mt-5 flex items-start gap-3 rounded-xl border border-primary-200 bg-primary-50 p-4 text-primary-900">
            <i className="ri-checkbox-circle-line mt-0.5 text-xl" />
            <div className="min-w-0 text-base">
              <p className="font-bold">這次健診已保存。</p>
              {reused ? (
                <p className="mt-1 text-primary-800">此份報告已保存，未重複建立。</p>
              ) : (
                <p className="mt-1 text-primary-800">可在下方「此瀏覽器健診紀錄」查看。</p>
              )}
            </div>
          </div>
        )}

        {status === 'error' && errorMessage && (
          <div className="mt-5 flex items-start gap-3 rounded-xl border border-rose-200 bg-rose-50 p-4 text-rose-700">
            <i className="ri-error-warning-line mt-0.5 text-xl" />
            <p className="text-base">{errorMessage}</p>
          </div>
        )}

        {canReadback && (
          <div className="mt-5 flex flex-wrap items-center gap-3 border-t border-background-200 pt-5">
            <button
              type="button"
              onClick={onReadback}
              disabled={readbackStatus === 'loading'}
              className="inline-flex h-12 w-full cursor-pointer items-center justify-center gap-2 whitespace-nowrap rounded-xl border border-primary-300 bg-background-50 px-5 text-base font-semibold text-primary-800 transition-colors hover:bg-primary-50 disabled:cursor-not-allowed disabled:opacity-60 sm:w-auto"
            >
              {readbackStatus === 'loading' ? (
                <>
                  <i className="ri-loader-4-line animate-spin text-lg" />
                  正在讀取…
                </>
              ) : (
                <>
                  <i className="ri-history-line text-lg" />
                  讀取已保存報告
                </>
              )}
            </button>
            <span className="text-sm text-foreground-500">確認這份報告已完整保存。</span>
          </div>
        )}

        {readbackStatus === 'error' && readbackError && (
          <div className="mt-4 flex items-start gap-3 rounded-xl border border-rose-200 bg-rose-50 p-4 text-rose-700">
            <i className="ri-error-warning-line mt-0.5 text-xl" />
            <p className="text-base">{readbackError}</p>
          </div>
        )}
      </div>
    </section>
  );
}